import { useQuery } from "@tanstack/react-query";
import endpointMaker from "configuration/endpoints";
import { createQueryKey, QUERY_KEYS } from "helpers/useQuery/queryKeys";
import request from "http/request";
import { AssetsArray } from "model/creative/properties";
import { useAccountId, useOrganizationId } from "redux/auth";

export type CreativeSnapshot = {
  assets?: AssetsArray;
  aspectRatios?: { values?: string[] };
};

type SnapshotResponse = {
  snapshot?: { creative?: CreativeSnapshot };
};

/**
 * Snapshots are immutable once written, so they never need refetching. A missing
 * one 404s and the caller falls back to whatever it can say without the document.
 */
export const useCreativeSnapshot = (
  snapshotId?: string
): CreativeSnapshot | null => {
  const organizationId = useOrganizationId();
  const accountId = useAccountId();

  const { data } = useQuery({
    queryKey: createQueryKey(
      QUERY_KEYS.CREATIVE_SNAPSHOT,
      organizationId,
      accountId,
      snapshotId
    ),
    queryFn: () =>
      request
        .getJson(
          endpointMaker.api.creativeSnapshot(
            organizationId,
            accountId,
            snapshotId
          )
        )
        .then(
          (response: SnapshotResponse) => response?.snapshot?.creative ?? null
        ),
    enabled: !!snapshotId && !!organizationId && !!accountId,
    staleTime: Infinity,
    retry: false,
  });

  return data ?? null;
};
